import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { sampleProperties, propertyTypes, featuredProperties } from "@/mocks/properties";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const categoryImages: Record<string, { src: string; alt: string }> = {
  Kominka: {
    src: "https://readdy.ai/api/search-image?query=Traditional%20Japanese%20kominka%20farmhouse%20exterior%20with%20exposed%20wooden%20beams%20and%20dark%20tiled%20roof%20small%20garden%20soft%20overcast%20light%20muted%20warm%20palette%20documentary%20architectural%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-kominka&orientation=landscape",
    alt: "Traditional Japanese kominka farmhouse exterior, representative category image",
  },
  Houses: {
    src: "https://readdy.ai/api/search-image?query=Modest%20two-storey%20Japanese%20suburban%20house%20on%20a%20quiet%20residential%20lane%20overcast%20sky%20understated%20muted%20warm%20tones%20documentary%20architectural%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-houses&orientation=landscape",
    alt: "Japanese suburban house on a quiet lane, representative category image",
  },
  "Apartment blocks": {
    src: "https://readdy.ai/api/search-image?query=Small%20two-storey%20Japanese%20apartment%20building%20in%20a%20regional%20town%20with%20simple%20architecture%20calm%20plain%20atmosphere%20overcast%20light%20muted%20warm%20tones%20documentary%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-apartment&orientation=landscape",
    alt: "Small Japanese apartment building in a regional town, representative category image",
  },
  Condos: {
    src: "https://readdy.ai/api/search-image?query=Bright%20minimal%20Japanese%20one%20room%20condo%20interior%20with%20clean%20lines%20soft%20daylight%20through%20large%20windows%20no%20clutter%20muted%20warm%20earth%20tones%20editorial%20interior%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-condo&orientation=landscape",
    alt: "Minimal Japanese condo interior with clean lines, representative category image",
  },
};

function parsePrice(p: string): number {
  const num = p.replace(/[¥,$]/g, "").replace(/,/g, "");
  return parseInt(num, 10) || 0;
}

export default function Properties() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("price-asc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { t, i18n } = useTranslation();

  let filtered =
    activeFilter === "all"
      ? [...sampleProperties]
      : sampleProperties.filter((p) => p.type === activeFilter);

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        p.headline.toLowerCase().includes(q) ||
        p.region.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }

  filtered.sort((a, b) => {
    switch (sort) {
      case "price-asc":
        return parsePrice(a.priceJPY) - parsePrice(b.priceJPY);
      case "price-desc":
        return parsePrice(b.priceJPY) - parsePrice(a.priceJPY);
      case "year-desc":
        return b.yearBuilt - a.yearBuilt;
      case "year-asc":
        return a.yearBuilt - b.yearBuilt;
      default:
        return 0;
    }
  });

  const sortOptions = [
    { label: t("sort.priceAsc"), value: "price-asc" },
    { label: t("sort.priceDesc"), value: "price-desc" },
    { label: t("sort.yearDesc"), value: "year-desc" },
    { label: t("sort.yearAsc"), value: "year-asc" },
  ];

  const typeLabelMap: Record<string, string> = {
    all: t("type.all"),
    Kominka: t("type.kominka"),
    Houses: t("type.detached"),
    "Apartment blocks": t("type.apartmentBlocks"),
    Condos: t("type.condos"),
  };

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main className="pt-24 md:pt-32 pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-4">
                {t("props.sectionLabel")}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-5 leading-tight">
                {t("props.title")}
              </h1>
              <p className="text-lg text-ink/50 font-light max-w-2xl">
                {t("props.intro")}
              </p>
            </div>
            <a
              href="/noren-property-portfolio.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-3 bg-ink text-paper px-5 py-4 hover:bg-vermilion transition-colors duration-300 cursor-pointer group whitespace-nowrap"
            >
              <i className="ri-file-pdf-2-line text-2xl text-gold mt-0.5"></i>
              <div className="text-left">
                <p className="text-xs uppercase tracking-wider text-gold mb-0.5">Send-ready</p>
                <p className="text-sm font-semibold leading-tight">Download portfolio PDF</p>
                <p className="text-xs text-paper/50 mt-0.5">29 pages · all 59 listings · 7 MB</p>
              </div>
              <i className="ri-download-line text-sm ml-1 mt-1.5 group-hover:translate-y-0.5 transition-transform"></i>
            </a>
          </div>

          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-ink/30 text-sm"></i>
              <input
                type="text"
                placeholder={t("label.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-hairline bg-white text-ink text-sm focus:outline-none focus:border-gold transition-colors"
              />
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <span className="text-xs text-ink/40 uppercase tracking-wider">{t("label.sort")}</span>
              <div className="flex items-center gap-1 bg-white border border-hairline p-0.5">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSort(opt.value)}
                    className={`px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sort === opt.value
                        ? "bg-ink text-paper"
                        : "text-ink/50 hover:text-ink"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="lg:hidden inline-flex items-center gap-2 bg-white border border-hairline px-4 py-2.5 text-sm text-ink/70 cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              {t("label.filters")}
            </button>
          </div>

          {/* Mobile filters panel */}
          {mobileFiltersOpen && (
            <div className="lg:hidden bg-white border border-hairline p-4 mb-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/40 mb-3">{t("label.type")}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {propertyTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setActiveFilter(type.value);
                      setMobileFiltersOpen(false);
                    }}
                    className={`px-3 py-1.5 text-xs font-medium border transition-all whitespace-nowrap cursor-pointer ${
                      activeFilter === type.value
                        ? "bg-ink text-paper border-ink"
                        : "bg-white text-ink/60 border-hairline"
                    }`}
                  >
                    {typeLabelMap[type.value] || type.label}
                  </button>
                ))}
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/40 mb-3">{t("label.sort")}</p>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setSort(opt.value);
                      setMobileFiltersOpen(false);
                    }}
                    className={`px-3 py-1.5 text-xs font-medium border transition-all whitespace-nowrap cursor-pointer ${
                      sort === opt.value
                        ? "bg-ink text-paper border-ink"
                        : "bg-white text-ink/60 border-hairline"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop type filters */}
          <div className="hidden lg:flex flex-wrap gap-2 mb-8">
            {propertyTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setActiveFilter(type.value)}
                className={`px-4 py-2 text-sm font-medium border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === type.value
                    ? "bg-ink text-paper border-ink"
                    : "bg-white text-ink/60 border-hairline hover:border-ink/30"
                }`}
              >
                {typeLabelMap[type.value] || type.label}
              </button>
            ))}
          </div>

          {/* Featured section — show only when no filter/search applied */}
          {activeFilter === "all" && !search.trim() && featuredProperties.length > 0 && (
            <section className="mb-14 -mx-4 md:-mx-8 lg:-mx-12 px-4 md:px-8 lg:px-12 py-10 bg-gradient-to-br from-vermilion/[0.04] via-paper to-gold/[0.04] border-y border-vermilion/15">
              <div className="flex items-end justify-between gap-6 mb-7 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-star-fill text-vermilion"></i>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-vermilion">
                      Featured · おすすめ
                    </p>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-ink leading-tight">
                    Hand-picked by our Tokyo partner.
                  </h2>
                  <p className="text-sm text-ink/50 mt-2 max-w-xl font-light">
                    {featuredProperties.length} priority listings from <span className="font-medium text-ink">日天不動産 (Nitten Real Estate)</span>, our boots-on-the-ground partner specializing in mid-priced used homes.
                  </p>
                </div>
                <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-ink/40 px-3 py-1.5 border border-hairline rounded-full bg-white/60">
                  <i className="ri-shield-check-line"></i>
                  Verified partner inventory
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {featuredProperties.map((property) => {
                  const catImg = categoryImages[property.type] || categoryImages.Houses;
                  return (
                    <div
                      key={property.id}
                      className="group bg-white border-2 border-vermilion/30 hover:border-vermilion/60 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-lg"
                    >
                      <div className="relative overflow-hidden bg-paper-secondary">
                        <img
                          src={property.image || catImg.src}
                          alt={property.headline}
                          loading="lazy"
                          className="w-full h-48 object-cover object-center group-hover:scale-[1.03] transition-transform duration-500"
                          style={{ objectPosition: "center" }}
                        />
                        <span className="absolute top-3 left-3 bg-vermilion text-paper text-xs font-semibold px-3 py-1.5 flex items-center gap-1">
                          <i className="ri-star-fill text-[10px]"></i>
                          おすすめ
                        </span>
                        <span className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-xs font-semibold px-3 py-1.5 text-ink/80 border border-hairline">
                          {property.id}
                        </span>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-vermilion font-semibold uppercase tracking-wider">
                            {property.typeLabel}
                          </span>
                          <span className="text-xs text-ink/30">
                            {property.yearBuilt > 0 ? property.yearBuilt : "Year TBD"}
                          </span>
                        </div>
                        <p className="font-serif text-base font-medium text-ink/90 mb-1 leading-snug">
                          {property.headline}
                        </p>
                        <p className="text-sm text-ink/40 leading-relaxed mb-3 line-clamp-2 font-light">
                          {property.description}
                        </p>
                        <p className="text-xs text-ink/30 mb-3">{property.region}</p>
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="font-serif text-lg font-semibold text-ink">
                            {property.priceUSD}
                          </span>
                          <span className="text-xs text-ink/40">
                            ({property.priceJPY})
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-ink/40 mb-4 font-light flex-wrap">
                          {property.area && property.area !== "—" && (
                            <span className="flex items-center gap-1">
                              <i className="ri-ruler-line"></i>
                              {property.area}
                            </span>
                          )}
                          {property.yieldPct && (
                            <span className="flex items-center gap-1 text-vermilion font-semibold">
                              <i className="ri-line-chart-line"></i>
                              {property.yieldPct.toFixed(1)}%
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <Link
                            to={`/properties/${property.id}`}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-vermilion hover:text-ink transition-colors duration-300"
                          >
                            {t("btn.viewDetails")}
                            <i className="ri-arrow-right-line"></i>
                          </Link>
                          <a
                            href={buildWhatsAppUrl(property.id, property.headline, i18n.language)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 transition-colors duration-300"
                          >
                            <i className="ri-whatsapp-line"></i>
                            {t("btn.whatsapp")}
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Results count */}
          <p className="text-xs text-ink/40 mb-6">
            {activeFilter === "all" && !search.trim()
              ? `Full inventory · ${filtered.length} listings`
              : t("label.showing", { count: filtered.length, total: sampleProperties.length })}
          </p>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {(activeFilter === "all" && !search.trim()
              ? filtered.filter((p) => !p.featured)
              : filtered
            ).map((property) => {
              const catImg = categoryImages[property.type] || categoryImages.Houses;
              return (
                <div
                  key={property.id}
                  className="group bg-white border border-hairline hover:border-ink/20 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative overflow-hidden bg-paper-secondary">
                    <img
                      src={property.image || catImg.src}
                      alt={property.image ? property.headline : catImg.alt}
                      loading="lazy"
                      className="w-full h-48 object-cover object-center group-hover:scale-[1.03] transition-transform duration-500"
                      style={{ objectPosition: "center" }}
                    />
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-xs font-semibold px-3 py-1.5 text-ink/80 border border-hairline">
                      {property.id}
                    </span>
                    <span className="absolute top-3 right-3 bg-vermilion text-paper text-xs font-semibold px-3 py-1.5">
                      {t("label.offMarket")}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-ink/40 uppercase tracking-wider">
                        {property.typeLabel}
                      </span>
                      <span className="text-xs text-ink/30">
                        {property.yearBuilt > 0 ? property.yearBuilt : "Year TBD"}
                      </span>
                    </div>
                    <p className="font-serif text-base font-medium text-ink/90 mb-1 leading-snug">
                      {property.headline}
                    </p>
                    <p className="text-sm text-ink/40 leading-relaxed mb-3 line-clamp-2 font-light">
                      {property.description}
                    </p>
                    <p className="text-xs text-ink/30 mb-3">{property.region}</p>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-serif text-lg font-semibold text-ink">
                        {property.priceUSD}
                      </span>
                      <span className="text-xs text-ink/40">
                        ({property.priceJPY})
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-ink/40 mb-4 font-light flex-wrap">
                      {property.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <i className="ri-hotel-bed-line"></i>
                          {t("label.bedrooms", { count: property.bedrooms })}
                        </span>
                      )}
                      {property.type === "Apartment blocks" && property.highlight && (
                        <span className="flex items-center gap-1">
                          <i className="ri-building-line"></i>
                          {property.highlight}
                        </span>
                      )}
                      {property.area && property.area !== "—" && (
                        <span className="flex items-center gap-1">
                          <i className="ri-ruler-line"></i>
                          {property.area}
                        </span>
                      )}
                      {property.yieldPct && (
                        <span className="flex items-center gap-1 text-gold">
                          <i className="ri-line-chart-line"></i>
                          {property.yieldPct.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/properties/${property.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-gold transition-colors duration-300"
                      >
                        {t("btn.viewDetails")}
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                      <a
                        href={buildWhatsAppUrl(property.id, property.headline, i18n.language)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 transition-colors duration-300"
                      >
                        <i className="ri-whatsapp-line"></i>
                        {t("btn.whatsapp")}
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-ink/40 font-light">{t("label.noMatch")}</p>
              <button
                onClick={() => {
                  setActiveFilter("all");
                  setSearch("");
                }}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-ink transition-colors cursor-pointer"
              >
                {t("btn.clearFilters")}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}